import { useState, useMemo } from 'react';

import type { EmpresaPlataforma } from '@/mocks/backoffice/empresas';
import {
  configuracoesCampanhas,
  camposConfiguracao,
  valoresPadraoPlataforma,
  type ConfigCampanha,
  type CategoriaConfig,
} from '@/mocks/backoffice/configuracoesCampanhas';
import { campanhasBackoffice, getStatusColor, getStatusLabel, getTipoLabel } from '@/mocks/backoffice/campanhasBackoffice';
import { registrarAuditoria } from '@/mocks/backoffice/auditoriaHelpers';

const categorias: { id: CategoriaConfig; label: string; icon: string }[] = [
  { id: 'taxas', label: 'Taxas', icon: 'ri-percent-line' },
  { id: 'limites', label: 'Limites', icon: 'ri-ruler-line' },
  { id: 'regras', label: 'Regras', icon: 'ri-file-list-3-line' },
  { id: 'geral', label: 'Geral', icon: 'ri-settings-3-line' },
];

interface EmpresaCampanhasConfigProps {
  empresa: EmpresaPlataforma;
  onShowToast: (msg: string, type: 'success' | 'error') => void;
}

export default function EmpresaCampanhasConfig({ empresa, onShowToast }: EmpresaCampanhasConfigProps) {
  const [configs, setConfigs] = useState<ConfigCampanha[]>([...configuracoesCampanhas]);
  const [campanhaSelecionada, setCampanhaSelecionada] = useState<string>('');
  const [activeCategoria, setActiveCategoria] = useState<CategoriaConfig>('taxas');
  const [editando, setEditando] = useState<string | null>(null);
  const [editValor, setEditValor] = useState<string | number | boolean>('');
  const [mostrarModalLote, setMostrarModalLote] = useState(false);

  // Apenas campanhas desta empresa
  const campanhasDaEmpresa = useMemo(() => {
    return campanhasBackoffice.filter((c) => c.empresaId === empresa.id);
  }, [empresa.id]);

  // Seleciona a primeira campanha automaticamente se não houver seleção
  const campanhaAtiva = campanhaSelecionada || (campanhasDaEmpresa[0]?.id ?? '');

  const configAtual = configs.find((c) => c.campanhaId === campanhaAtiva);
  const campanhaInfo = campanhasBackoffice.find((c) => c.id === campanhaAtiva);

  const camposFiltrados = useMemo(() => {
    return camposConfiguracao.filter((c) => c.categoria === activeCategoria);
  }, [activeCategoria]);

  const handleToggleHeranca = () => {
    if (!configAtual) return;
    const idx = configs.findIndex((c) => c.campanhaId === campanhaAtiva);
    if (idx === -1) return;

    const novoHerda = !configAtual.herdaPlataforma;
    configs[idx].herdaPlataforma = novoHerda;
    configs[idx].ultima_alteracao = new Date().toISOString();
    configs[idx].alterado_por = 'André Costa';
    setConfigs([...configs]);

    registrarAuditoria(
      'alterar_configuracao',
      'configuracoes',
      `${novoHerda ? 'Ativou' : 'Desativou'} herança da empresa para campanha ${configAtual.campanhaNome} (${campanhaAtiva})`,
      'André Costa',
      'admin',
      campanhaAtiva,
      'campanha'
    );

    onShowToast(`${novoHerda ? 'Herança ativada' : 'Herança desativada'} para ${configAtual.campanhaNome}`, 'success');
  };

  const handleEdit = (chave: string) => {
    if (!configAtual || configAtual.herdaPlataforma) return;
    const valor = configAtual[activeCategoria][chave as keyof (typeof configAtual)[typeof activeCategoria]];
    setEditando(chave);
    setEditValor(valor as string | number | boolean);
  };

  const handleSave = (chave: string) => {
    if (!configAtual || configAtual.herdaPlataforma) return;
    const idx = configs.findIndex((c) => c.campanhaId === campanhaAtiva);
    if (idx === -1) return;

    const campo = camposConfiguracao.find((c) => c.chave === chave);
    const valorAnterior = configAtual[activeCategoria][chave as keyof (typeof configAtual)[typeof activeCategoria]];

    (configs[idx][activeCategoria] as Record<string, string | number | boolean>)[chave] = editValor;
    configs[idx].ultima_alteracao = new Date().toISOString();
    configs[idx].alterado_por = 'André Costa';
    setConfigs([...configs]);
    setEditando(null);

    registrarAuditoria(
      'alterar_configuracao',
      'configuracoes',
      `Alterou ${campo?.label || chave} da campanha ${configAtual.campanhaNome}: ${valorAnterior} → ${editValor}`,
      'André Costa',
      'admin',
      campanhaAtiva,
      'campanha'
    );

    onShowToast('Configuração salva com sucesso', 'success');
  };

  const handleCancel = () => {
    setEditando(null);
  };

  const getValorAtual = (chave: string, tipo: string): { valor: string | number | boolean; herda: boolean; diferente: boolean } => {
    if (!configAtual) return { valor: '', herda: true, diferente: false };
    if (configAtual.herdaPlataforma) {
      const padrao = valoresPadraoPlataforma[activeCategoria][chave as keyof (typeof valoresPadraoPlataforma)[typeof activeCategoria]];
      return { valor: padrao as string | number | boolean, herda: true, diferente: false };
    }
    const valor = configAtual[activeCategoria][chave as keyof (typeof configAtual)[typeof activeCategoria]];
    const padrao = valoresPadraoPlataforma[activeCategoria][chave as keyof (typeof valoresPadraoPlataforma)[typeof activeCategoria]];
    return {
      valor: valor as string | number | boolean,
      herda: false,
      diferente: valor !== padrao,
    };
  };

  const aplicarLote = () => {
    setMostrarModalLote(false);
    onShowToast(`Configurações aplicadas em lote para todos os participantes de ${configAtual?.campanhaNome}`, 'success');
  };

  const categoriaAtual = categorias.find((c) => c.id === activeCategoria);

  return (
    <div className="space-y-5">
      {/* Header da campanha */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Selecionar Campanha da {empresa.nome_fantasia}</label>
            <select
              value={campanhaAtiva}
              onChange={(e) => { setCampanhaSelecionada(e.target.value); setEditando(null); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
            >
              {campanhasDaEmpresa.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome} — {c.participantesAtivos} ativos
                </option>
              ))}
            </select>
          </div>
          {campanhaInfo && (
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${getStatusColor(campanhaInfo.status)}`}>{getStatusLabel(campanhaInfo.status)}</span>
              <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-stone-100 text-stone-700">{getTipoLabel(campanhaInfo.tipo)}</span>
            </div>
          )}
        </div>
        {campanhaInfo && (
          <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
            <span className="flex items-center space-x-1">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-group-line text-sm"></i></div>
              <span>{campanhaInfo.participantesAtivos} / {campanhaInfo.participantesTotal} participantes</span>
            </span>
            <span className="flex items-center space-x-1">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-wallet-line text-sm"></i></div>
              <span>{campanhaInfo.pontosDisponiveis.toLocaleString('pt-BR')} pts disponíveis</span>
            </span>
            <span className="flex items-center space-x-1">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-sm"></i></div>
              <span>{new Date(campanhaInfo.dataInicio).toLocaleDateString('pt-BR')} — {new Date(campanhaInfo.dataFim).toLocaleDateString('pt-BR')}</span>
            </span>
          </div>
        )}
      </div>

      {/* Toggle herança */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${configAtual?.herdaPlataforma ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${configAtual?.herdaPlataforma ? 'ri-link' : 'ri-link-unlink'} ${configAtual?.herdaPlataforma ? 'text-emerald-600' : 'text-amber-600'} text-lg`}></i>
              </div>
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">
                {configAtual?.herdaPlataforma ? 'Herda configurações da empresa' : 'Configurações personalizadas'}
              </p>
              <p className="text-xs text-gray-500">
                {configAtual?.herdaPlataforma
                  ? 'Usando valores padrão da empresa. Desative para personalizar esta campanha.'
                  : `Última alteração por ${configAtual?.alterado_por || '-'} em ${configAtual?.ultima_alteracao ? new Date(configAtual.ultima_alteracao).toLocaleDateString('pt-BR') : '-'}`}
              </p>
            </div>
          </div>
          <button
            onClick={handleToggleHeranca}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
              configAtual?.herdaPlataforma ? 'bg-emerald-500' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${configAtual?.herdaPlataforma ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* Categorias */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {categorias.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategoria(cat.id)}
            disabled={configAtual?.herdaPlataforma}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
              activeCategoria === cat.id ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-100 hover:border-gray-200'
            } ${configAtual?.herdaPlataforma ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${activeCategoria === cat.id ? 'bg-emerald-100' : 'bg-gray-50'}`}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${cat.icon} ${activeCategoria === cat.id ? 'text-emerald-600' : 'text-gray-400'} text-lg`}></i>
              </div>
            </div>
            <p className={`text-sm font-bold ${activeCategoria === cat.id ? 'text-emerald-700' : 'text-gray-700'}`}>{cat.label}</p>
          </button>
        ))}
      </div>

      {/* Campos */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${categoriaAtual?.icon} text-emerald-600 text-sm`}></i>
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-800">{categoriaAtual?.label}</h2>
              <p className="text-xs text-gray-400">
                {configAtual?.herdaPlataforma ? 'Valores herdados da empresa (somente leitura)' : 'Edite os valores personalizados'}
              </p>
            </div>
          </div>
          {!configAtual?.herdaPlataforma && (
            <button
              onClick={() => setMostrarModalLote(true)}
              className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="flex items-center space-x-1.5">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-sm"></i></div>
                <span>Aplicar em Lote</span>
              </div>
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-50">
          {camposFiltrados.map((campo) => {
            const { valor, herda, diferente } = getValorAtual(campo.chave, campo.tipo);
            const isEditando = editando === campo.chave;
            const isReadOnly = configAtual?.herdaPlataforma ?? true;

            return (
              <div key={campo.chave} className={`px-5 py-4 flex items-start justify-between gap-4 ${herda ? 'bg-gray-50/50' : diferente ? 'bg-amber-50/30' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-bold text-gray-800">{campo.label}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-bold uppercase">{campo.tipo}</span>
                    {herda && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-600 font-bold">Empresa</span>
                    )}
                    {!herda && diferente && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 font-bold">Diferente</span>
                    )}
                    {!herda && !diferente && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 font-bold">Igual à empresa</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{campo.descricao}</p>
                </div>
                <div className="flex items-center space-x-3 flex-shrink-0">
                  {isEditando && !isReadOnly ? (
                    <div className="flex items-center space-x-2">
                      {campo.tipo === 'boolean' ? (
                        <input
                          type="checkbox"
                          checked={editValor === true || editValor === 'true'}
                          onChange={(e) => setEditValor(e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300 text-emerald-600"
                        />
                      ) : (
                        <input
                          type={campo.tipo === 'porcentagem' || campo.tipo === 'numero' ? 'number' : 'text'}
                          value={editValor as string | number}
                          onChange={(e) => setEditValor(campo.tipo === 'boolean' ? e.target.checked : campo.tipo === 'numero' || campo.tipo === 'porcentagem' ? Number(e.target.value) : e.target.value)}
                          className="w-32 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          step={campo.tipo === 'porcentagem' ? '0.01' : campo.tipo === 'numero' ? '1' : undefined}
                        />
                      )}
                      <button onClick={() => handleSave(campo.chave)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 cursor-pointer">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-sm"></i></div>
                      </button>
                      <button onClick={handleCancel} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line text-sm"></i></div>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        {campo.tipo === 'boolean' ? (
                          valor === true || valor === 'true' ? (
                            <span className="inline-flex items-center space-x-1 text-emerald-600">
                              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-sm"></i></div>
                              <span className="text-xs font-bold">Ativo</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center space-x-1 text-gray-400">
                              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line text-sm"></i></div>
                              <span className="text-xs font-bold">Inativo</span>
                            </span>
                          )
                        ) : campo.tipo === 'porcentagem' ? (
                          <span className="text-sm font-bold text-gray-800">{valor}%</span>
                        ) : campo.tipo === 'numero' ? (
                          <span className="text-sm font-bold text-gray-800">{Number(valor).toLocaleString('pt-BR')}</span>
                        ) : (
                          <span className="text-sm text-gray-800">{String(valor)}</span>
                        )}
                      </div>
                      {!isReadOnly && (
                        <button onClick={() => handleEdit(campo.chave)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-pencil-line text-sm"></i></div>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Link para premiados */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 flex items-center justify-center mt-0.5">
            <i className="ri-information-line text-blue-600 text-sm"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-blue-900">Configuração por Premiado</p>
            <p className="text-xs text-blue-700 mt-1">
              As configurações definidas aqui são aplicadas em lote para todos os participantes desta campanha.
              Você pode fazer ajustes individuais por campanha na aba <strong>Premiados</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Modal aplicação em lote */}
      {mostrarModalLote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Aplicar em Lote</h3>
              <button onClick={() => setMostrarModalLote(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <div className="w-6 h-6 flex items-center justify-center"><i className="ri-close-line text-xl"></i></div>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              As configurações atuais de <strong>{configAtual?.campanhaNome}</strong> serão aplicadas para todos os <strong>{campanhaInfo?.participantesAtivos}</strong> participantes ativos desta campanha.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-alert-line text-amber-600 text-sm"></i></div>
                <span className="text-xs text-amber-700">Participantes com configuração individual não serão afetados.</span>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <button onClick={() => setMostrarModalLote(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={aplicarLote} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-sm"></i></div>
                  <span>Aplicar em Lote</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}